import { useEffect, useRef } from "react";
import { getPlatform } from "@/platform";
import { useUISlice } from "@/models/useUISlice";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "./Icons";
import { resetMetaThemeColor, setMetaThemeColor } from "@/models/useTheme";
import useKeyboard from "@/models/useKeyboard";
import {
  attachModalKeyboardDismiss,
  attachModalPointerDismiss,
  canStartModalSwipe,
  getModalSwipeEnd,
} from "@/dom-events";

const LoadingSpinner = () => (
  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export interface ModalContainerProps {
  title: string;
  onDismiss: () => void;
  onPrimaryButtonPress?: () => void;
  onLeftButtonPress?: () => void;
  primaryButtonText?: string;
  children: React.ReactNode;
  autoFocus?: boolean;
  modalWidth?: number;
  modalMaxWidth?: number;
  statusBarBgColor?: string;
  hideDismissIcon?: boolean;
  subtitle?: string;
  primaryButtonDisabled?: boolean;
  destructive?: boolean;
  contentClassName?: string;
  removeBottomPadding?: boolean;
  cancelText?: string;
  className?: string;
  hideCancel?: boolean;
  disableModalStyle?: boolean;
  shouldPersistBackdropClick?: boolean;
  loading?: boolean;
  fullScreen?: boolean;
  removeHeader?: boolean;
  /** Shown above the centered dialog on wide screens; phones have no room. */
  desktopHeader?: React.ReactNode;
}

let activeBodyScrollLocks = 0;
let previousBodyOverflow = "";

const ModalContainer = (props: ModalContainerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const { t } = useTranslation();
  const effectiveIsMobile = props.disableModalStyle ? false : isMobile;
  const sideHeader = Boolean(props.desktopHeader) && !effectiveIsMobile;
  const dialogMaxWidth =
    props.fullScreen || effectiveIsMobile
      ? "100%"
      : props.modalMaxWidth
        ? `${props.modalMaxWidth}px`
        : props.modalWidth
          ? `${props.modalWidth}px`
          : "500px";
  const { isVisible: isKeyboardVisible } = useKeyboard();
  const shouldRemoveBottomPadding = props.removeBottomPadding || isKeyboardVisible;

  // Swipe to close logic for iOS
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const isDragging = useRef(false);
  const currentTranslateY = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Check if the user is scrolling content
    let target = e.target as HTMLElement;
    let canScrollUp = false;

    // Traverse up to the contentRef to check for scrollable areas
    while (target && target !== contentRef.current) {
      if (target.scrollHeight > target.clientHeight && target.scrollTop > 0) {
        canScrollUp = true;
        break;
      }
      target = target.parentElement as HTMLElement;
    }

    if (
      !canStartModalSwipe({
        platform: getPlatform(),
        shouldPersist: Boolean(props.shouldPersistBackdropClick),
        canScrollUp,
      })
    )
      return;

    isDragging.current = true;
    dragStartY.current = e.touches[0].clientY;

    if (contentRef.current) {
      contentRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !contentRef.current) return;

    const deltaY = e.touches[0].clientY - dragStartY.current;

    // Only allow dragging down
    if (deltaY > 0) {
      currentTranslateY.current = deltaY;
      contentRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || !contentRef.current) return;
    isDragging.current = false;

    if (getModalSwipeEnd(currentTranslateY.current) === "dismiss") {
      props.onDismiss();
    } else {
      // Reset
      contentRef.current.style.transition = "transform 0.3s ease-out";
      contentRef.current.style.transform = "";
      currentTranslateY.current = 0;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (props.shouldPersistBackdropClick) return;

      if (ref.current && !ref.current.contains(event.target as Node)) {
        props.onDismiss();
      }
    };

    return attachModalPointerDismiss(document, handleClickOutside);
  }, [props.shouldPersistBackdropClick, props.onDismiss]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (activeBodyScrollLocks === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    activeBodyScrollLocks += 1;

    return () => {
      activeBodyScrollLocks = Math.max(0, activeBodyScrollLocks - 1);
      if (activeBodyScrollLocks === 0) {
        document.body.style.overflow = previousBodyOverflow;
      }
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    if (props.shouldPersistBackdropClick) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        props.onDismiss();
      }
    };

    return attachModalKeyboardDismiss(document, handleKeyDown);
  }, [props.shouldPersistBackdropClick, props.onDismiss]);

  // Update theme-color meta tag
  useEffect(() => {
    if (props.statusBarBgColor) {
      setMetaThemeColor(props.statusBarBgColor);
    } else {
      // Use CSS variable for modal theme color
      const computedColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--theme-color-modal")
        .trim();
      if (computedColor) {
        setMetaThemeColor(computedColor);
      }
    }

    return () => {
      resetMetaThemeColor();
    };
  }, [props.statusBarBgColor]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (props.shouldPersistBackdropClick) return;
    if (e.target === e.currentTarget) {
      props.onDismiss();
    }
  };

  return (
    <div
      data-action-id="modal.backdrop.dismiss"
      className={`fixed inset-0 z-[1000] flex ${
        effectiveIsMobile
          ? "items-end"
          : props.fullScreen
            ? "items-center justify-center"
            : "items-center justify-center p-4"
      }`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        data-action-id="modal.backdrop.dismiss"
        className="scrim absolute inset-0"
        onClick={props.shouldPersistBackdropClick ? undefined : props.onDismiss}
      />

      {/* Modal wrapper */}
      <div
        ref={ref}
        className={`relative w-full ${
          effectiveIsMobile
            ? ""
            : sideHeader
              ? "flex flex-col items-center justify-center lg:flex-row lg:gap-12"
              : "flex items-center justify-center"
        }`}
        style={{ maxWidth: sideHeader ? "100%" : dialogMaxWidth }}
      >
        {sideHeader && props.desktopHeader}
        {/* Modal content */}
      <div
        ref={contentRef}
        data-action-id="modal.swipe"
        // With a header beside or above it, the dialog carries its own cap so
        // the wrapper can be wide enough for both.
        style={sideHeader ? { maxWidth: dialogMaxWidth } : undefined}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`
          glass-thick w-full max-h-full overflow-y-auto
            ${
              props.fullScreen
                ? "h-screen rounded-none px-6 py-10"
                : effectiveIsMobile
                  ? `rounded-t-[2.5rem] rounded-b-none px-6 pt-8 ${
                      shouldRemoveBottomPadding ? "pb-6" : "pb-24"
                    }`
                  : "rounded-[2.5rem] px-10 py-10"
            }
            ${props.className || ""}
          `}
        >
          {/* Grabber — the sheet is already swipe-to-dismiss; this is the
              affordance that says so. Decorative only. */}
          {effectiveIsMobile && !props.fullScreen && (
            <div
              aria-hidden="true"
              className="mx-auto -mt-3 mb-5 h-1 w-10 shrink-0 rounded-full bg-[var(--fill-3)]"
            />
          )}

          {/* Header */}
          {!props.removeHeader && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)] flex-1 mr-4 truncate">
                {props.title}
              </h2>

              {!props.hideDismissIcon && (
                <button
                  data-action-id="modal.dismiss"
                  onClick={props.onDismiss}
                  className="icon-btn icon-btn-quiet group relative"
                  aria-label={t("modalContainer.closeModal")}
                >
                  <CloseIcon className="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
            </div>
          )}

          {/* Subtitle */}
          {props.subtitle && (
            <p className="mb-8 text-base leading-relaxed text-[var(--text-secondary)] font-light">
              {props.subtitle}
            </p>
          )}

          {/* Children content */}
          <div className={`mb-8 ${props.contentClassName || ""}`}>
            {props.children}
          </div>

          {/* Button container */}
          {!props.hideCancel && (
            <div className={`flex items-center gap-4 ${effectiveIsMobile ? "flex-col-reverse" : "justify-end"}`}>
              <button
                data-action-id="modal.cancel"
                onClick={props.onLeftButtonPress || props.onDismiss}
                className={`btn btn-glass text-base ${effectiveIsMobile ? "w-full" : ""}`}
              >
                {props.cancelText || t("modalContainer.cancelButton")}
              </button>

              {props.onPrimaryButtonPress && (
                <button
                  data-action-id="modal.primary"
                  onClick={props.onPrimaryButtonPress}
                  disabled={props.primaryButtonDisabled || props.loading}
                  className={`
                    btn text-base
                    ${props.destructive ? "btn-danger" : "btn-primary"}
                    ${effectiveIsMobile ? "w-full" : ""}
                  `}
                >
                  {props.loading && <LoadingSpinner />}
                  {props.primaryButtonText}
                </button>
              )}
            </div>
          )}

          {/* Single primary button when hideCancel is true */}
          {props.hideCancel && props.onPrimaryButtonPress && (
            <button
              data-action-id="modal.primary"
              onClick={props.onPrimaryButtonPress}
              disabled={props.primaryButtonDisabled || props.loading}
              className={`
                btn w-full text-base
                ${props.destructive ? "btn-danger" : "btn-primary"}
              `}
            >
              {props.loading && <LoadingSpinner />}
              {props.primaryButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ModalContainer.OVERLAY_COLOR = "rgba(0, 0, 0, 0.4)";

export default ModalContainer;
